/**
 * Admin → KITE Pages → Edit. Three-pane WYSIWYG editor:
 *   [ Palette ] [ Canvas (drag to reorder, click to select) ] [ Inspector ]
 *
 * - dnd-kit for accessible drag-and-drop reordering
 * - immer for immutable history snapshots → undo/redo
 * - mobile/tablet/desktop preview width toggle
 * - Save Draft (PATCH) and Publish (POST :id/publish)
 *
 * The editor mirrors the public renderer pretty closely so what you see
 * is reasonably close to what gets published. Variables (`{{name}}`)
 * render literally in the editor so the author can see the placeholders.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { produce } from 'immer';
import {
  DndContext,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  closestCenter,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  arrayMove,
  verticalListSortingStrategy,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type {
  KitePage,
  KitePageBlock,
  KitePageBlockType,
  KitePageDocument,
} from '@xsc/shared';
import { api } from '../lib/api-client.js';
import { PreviewBlock, PreviewDocument } from '../components/kite/renderer.js';
import { BLOCK_PALETTE, newBlock } from '../components/kite/registry.js';
import { BlockInspector, ThemeInspector } from '../components/kite/inspector.js';

type DeviceMode = 'mobile' | 'tablet' | 'desktop';
// Standard responsive breakpoints used across the editor preview toggle:
// 360 (small phone), 768 (tablet portrait), 1280 (laptop). These match
// the most common device-emulation presets used by browser devtools so
// users see what real visitors see.
const DEVICE_WIDTHS: Record<DeviceMode, number> = { mobile: 360, tablet: 768, desktop: 1280 };

export function AdminKitePageEditPage(): JSX.Element {
  const { id = '' } = useParams<{ id: string }>();
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: ['kite-page', id],
    queryFn: async (): Promise<KitePage> =>
      (await api.get(`/v1/admin/kite-pages/${id}`)) as KitePage,
    enabled: !!id,
  });

  // Local editable document state, seeded from the server load. History
  // is a stack of snapshots for undo/redo; we replace stale snapshots
  // when the user redoes-then-edits (classic editor model).
  const [doc, setDoc] = useState<KitePageDocument | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [device, setDevice] = useState<DeviceMode>('desktop');
  const [dirty, setDirty] = useState(false);
  const historyRef = useRef<{ stack: KitePageDocument[]; index: number }>({ stack: [], index: -1 });

  // Reset local state whenever we navigate to a different page id so we
  // don't bleed one page's history/selection/dirty flag into another.
  useEffect(() => {
    setDoc(null);
    setSelectedId(null);
    setDirty(false);
    historyRef.current = { stack: [], index: -1 };
  }, [id]);

  useEffect(() => {
    if (query.data && doc === null) {
      setDoc(query.data.draft_document);
      historyRef.current = { stack: [query.data.draft_document], index: 0 };
    }
  }, [query.data, doc]);

  const update = useCallback((recipe: (d: KitePageDocument) => void) => {
    setDoc((current) => {
      if (!current) return current;
      const next = produce(current, recipe);
      // Truncate any redo branch and push the new snapshot.
      const h = historyRef.current;
      h.stack = h.stack.slice(0, h.index + 1);
      h.stack.push(next);
      h.index = h.stack.length - 1;
      // Cap history at 50 entries to bound memory on long sessions.
      if (h.stack.length > 50) {
        h.stack.shift();
        h.index -= 1;
      }
      setDirty(true);
      return next;
    });
  }, []);

  const undo = useCallback(() => {
    const h = historyRef.current;
    if (h.index > 0) {
      h.index -= 1;
      setDoc(h.stack[h.index]!);
      setDirty(true);
    }
  }, []);
  const redo = useCallback(() => {
    const h = historyRef.current;
    if (h.index < h.stack.length - 1) {
      h.index += 1;
      setDoc(h.stack[h.index]!);
      setDirty(true);
    }
  }, []);

  // Cmd/Ctrl-Z / Shift-Cmd/Ctrl-Z keyboard shortcuts for undo/redo.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const target = e.target as HTMLElement | null;
      // Don't hijack editing inside the inspector inputs.
      if (target && ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName)) return;
      const meta = e.metaKey || e.ctrlKey;
      if (meta && e.key === 'z' && !e.shiftKey) { e.preventDefault(); undo(); }
      if (meta && (e.key === 'y' || (e.key === 'z' && e.shiftKey))) { e.preventDefault(); redo(); }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [undo, redo]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const save = useMutation({
    mutationFn: async (): Promise<KitePage> =>
      (await api.patch(`/v1/admin/kite-pages/${id}`, { draft_document: doc })) as KitePage,
    onSuccess: () => {
      setDirty(false);
      qc.invalidateQueries({ queryKey: ['kite-pages'] });
      qc.invalidateQueries({ queryKey: ['kite-page', id] });
    },
  });

  const publish = useMutation({
    mutationFn: async (): Promise<KitePage> => {
      // Auto-save the draft before publishing so the published version
      // matches what's on screen, even if the user forgot Save Draft.
      if (dirty) await api.patch(`/v1/admin/kite-pages/${id}`, { draft_document: doc });
      return (await api.post(`/v1/admin/kite-pages/${id}/publish`, {})) as KitePage;
    },
    onSuccess: () => {
      setDirty(false);
      qc.invalidateQueries({ queryKey: ['kite-pages'] });
      qc.invalidateQueries({ queryKey: ['kite-page', id] });
    },
  });

  const addBlock = (type: KitePageBlockType) => {
    update((d) => { d.blocks.push(newBlock(type)); });
  };

  const onDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id || !doc) return;
    const oldIndex = doc.blocks.findIndex((b) => b.id === active.id);
    const newIndex = doc.blocks.findIndex((b) => b.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    update((d) => { d.blocks = arrayMove(d.blocks, oldIndex, newIndex); });
  };

  const selected = useMemo(
    () => doc?.blocks.find((b) => b.id === selectedId) ?? null,
    [doc, selectedId],
  );

  if (query.isLoading) return <p className="text-sm text-ink-muted">Loading editor…</p>;
  if (query.isError || !query.data) {
    return <p className="text-sm text-red-500">Failed to load page. <Link to="/admin/kite-pages" className="underline">Back</Link></p>;
  }
  if (!doc) return <p className="text-sm text-ink-muted">Preparing canvas…</p>;

  const page = query.data;
  const previewWidth = DEVICE_WIDTHS[device];
  const canUndo = historyRef.current.index > 0;
  const canRedo = historyRef.current.index < historyRef.current.stack.length - 1;

  return (
    <div className="space-y-3">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border pb-3">
        <div className="flex items-center gap-3">
          <Link to="/admin/kite-pages" className="text-sm text-accent underline">← Pages</Link>
          <h1 className="text-lg font-semibold">{page.title}</h1>
          {/* Show the live URL (published_slug) when available, falling back
              to draft slug for not-yet-published pages. Editing slug in the
              draft doesn't change the live URL until the next publish, so
              we surface both when they diverge. */}
          <span className="text-xs text-ink-muted font-mono">
            /k/{page.published_slug ?? page.slug}
            {page.published_slug && page.published_slug !== page.slug && (
              <span className="ml-2 text-amber-500" title="Draft slug differs from live; will move on next publish">
                (draft: /k/{page.slug})
              </span>
            )}
          </span>
          {dirty && <span className="text-xs text-amber-600">● Unsaved</span>}
          {!dirty && page.status === 'published' && (
            <span className="text-xs text-green-600">● Published v{page.published_version}</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <div className="flex border border-border rounded overflow-hidden text-xs">
            {(['mobile','tablet','desktop'] as DeviceMode[]).map((d) => (
              <button key={d}
                onClick={() => setDevice(d)}
                className={`px-3 py-1.5 ${device === d ? 'bg-accent text-white' : 'bg-bg hover:bg-muted'}`}
              >{d}</button>
            ))}
          </div>
          <button onClick={undo} disabled={!canUndo}
            className="px-2 py-1.5 text-sm border border-border rounded disabled:opacity-40"
            title="Undo (Cmd/Ctrl+Z)">↶</button>
          <button onClick={redo} disabled={!canRedo}
            className="px-2 py-1.5 text-sm border border-border rounded disabled:opacity-40"
            title="Redo (Cmd/Ctrl+Shift+Z)">↷</button>
          <button onClick={() => save.mutate()} disabled={save.isPending || !dirty}
            className="px-3 py-1.5 text-sm border border-border rounded disabled:opacity-50">
            {save.isPending ? 'Saving…' : 'Save draft'}
          </button>
          <button onClick={() => publish.mutate()} disabled={publish.isPending}
            className="px-3 py-1.5 text-sm rounded bg-accent text-white disabled:opacity-50">
            {publish.isPending ? 'Publishing…' : 'Publish'}
          </button>
        </div>
      </div>

      {save.isError && <p className="text-sm text-red-500">Save failed: {(save.error as Error).message}</p>}
      {publish.isError && <p className="text-sm text-red-500">Publish failed: {(publish.error as Error).message}</p>}

      {/* 3-pane layout */}
      <div className="grid grid-cols-12 gap-3" style={{ minHeight: '70vh' }}>
        {/* Palette */}
        <aside className="col-span-2 border border-border rounded p-2 space-y-1 overflow-y-auto">
          <h2 className="text-xs font-semibold text-ink-muted px-1 py-1">Add block</h2>
          {BLOCK_PALETTE.map((p) => (
            <button
              key={p.type}
              type="button"
              onClick={() => addBlock(p.type)}
              className="w-full text-left text-sm px-2 py-2 rounded hover:bg-muted flex items-start gap-2"
              title={p.description}
            >
              <span className="text-base leading-none">{p.icon}</span>
              <span>
                <span className="block font-medium">{p.label}</span>
                <span className="block text-xs text-ink-muted">{p.description}</span>
              </span>
            </button>
          ))}
        </aside>

        {/* Canvas */}
        <main className="col-span-7 border border-border rounded bg-muted/30 p-4 overflow-y-auto" style={{ maxHeight: '80vh' }}>
          <div className="mx-auto bg-white shadow-sm" style={{
            width: '100%', maxWidth: previewWidth, transition: 'max-width 200ms',
          }}>
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
              <SortableContext items={doc.blocks.map((b) => b.id)} strategy={verticalListSortingStrategy}>
                {doc.blocks.length === 0 && (
                  <div className="text-center text-ink-muted text-sm py-12">
                    Empty page. Add a block from the palette →
                  </div>
                )}
                {doc.blocks.map((block) => (
                  <SortableBlock
                    key={block.id}
                    block={block}
                    theme={doc.theme}
                    selected={selectedId === block.id}
                    onSelect={() => setSelectedId(block.id)}
                    onDelete={() => {
                      update((d) => { d.blocks = d.blocks.filter((b) => b.id !== block.id); });
                      if (selectedId === block.id) setSelectedId(null);
                    }}
                    onDuplicate={() => {
                      update((d) => {
                        const idx = d.blocks.findIndex((b) => b.id === block.id);
                        if (idx >= 0) {
                          const copy = JSON.parse(JSON.stringify(block)) as KitePageBlock;
                          copy.id = `b_${Math.random().toString(36).slice(2, 10)}`;
                          d.blocks.splice(idx + 1, 0, copy);
                        }
                      });
                    }}
                  />
                ))}
              </SortableContext>
            </DndContext>
          </div>
        </main>

        {/* Inspector */}
        <aside className="col-span-3 border border-border rounded p-3 overflow-y-auto" style={{ maxHeight: '80vh' }}>
          {selected ? (
            <BlockInspector
              block={selected}
              onChange={(next) => update((d) => {
                const idx = d.blocks.findIndex((b) => b.id === selected.id);
                if (idx >= 0) d.blocks[idx] = next;
              })}
            />
          ) : (
            <ThemeInspector theme={doc.theme} onChange={(theme) => update((d) => { d.theme = theme; })} />
          )}
        </aside>
      </div>

      {/* Hidden preview-only render (kept mounted for parity verification — not visible) */}
      <div style={{ display: 'none' }}><PreviewDocument doc={doc} /></div>
    </div>
  );
}

function SortableBlock({
  block,
  theme,
  selected,
  onSelect,
  onDelete,
  onDuplicate,
}: {
  block: KitePageBlock;
  theme: KitePageDocument['theme'];
  selected: boolean;
  onSelect: () => void;
  onDelete: () => void;
  onDuplicate: () => void;
}): JSX.Element {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: block.id });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    position: 'relative',
    outline: selected ? '2px solid #2563eb' : '2px solid transparent',
    outlineOffset: -2,
  };
  return (
    <div ref={setNodeRef} style={style} onClick={onSelect}>
      <div style={{
        position: 'absolute', top: 4, right: 4, zIndex: 10,
        display: 'flex', gap: 4,
        background: 'rgba(255,255,255,0.9)', borderRadius: 4, padding: 2,
        opacity: selected ? 1 : 0, transition: 'opacity 150ms',
      }}>
        <button
          type="button"
          {...attributes} {...listeners}
          className="px-2 py-0.5 text-xs cursor-grab active:cursor-grabbing"
          title="Drag to reorder"
          onClick={(e) => e.stopPropagation()}
        >⠿</button>
        <button
          type="button"
          className="px-2 py-0.5 text-xs"
          onClick={(e) => { e.stopPropagation(); onDuplicate(); }}
          title="Duplicate"
        >⎘</button>
        <button
          type="button"
          className="px-2 py-0.5 text-xs text-red-600"
          onClick={(e) => { e.stopPropagation(); onDelete(); }}
          title="Delete"
        >✕</button>
      </div>
      <PreviewBlock block={block} theme={theme} />
    </div>
  );
}
